import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Library, Search, BookmarkCheck, BarChart3, Download, ArrowRight, History, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { SubjectIcon, subjectGradient } from "@/components/SubjectIcon";

export default function Dashboard() {
    const { t, subjectLabel, userId, lang } = useApp();
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);
    const [bookmarkCount, setBookmarkCount] = useState(0);
    const nav = useNavigate();

    useEffect(() => {
        api.stats().then(setStats).catch(() => {});
        api.recentSearches(userId).then((r) => setRecent(r.items || [])).catch(() => {});
        api.listBookmarks(userId).then((r) => setBookmarkCount((r.items || []).length)).catch(() => {});
    }, [userId]);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:pb-32">
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="h-4 w-4 text-accent" />
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">{t.dashboard}</p>
                </div>
                <h1 className={`font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-6 ${lang === "od" ? "font-odia" : ""}`}>
                    {t.heroTitle}
                </h1>
            </motion.div>

            {/* Stat bento */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
                <StatTile to="/classes" label={t.totalClasses} value={stats?.total_classes} accent="from-primary to-secondary" icon={Library} />
                <StatTile to="/classes" label={t.totalSubjects} value={stats?.total_subjects} accent="from-accent to-emerald-500" icon={BookmarkCheck} />
                <StatTile to="/search" label={t.totalLOCodes} value={stats?.total_codes} accent="from-amber-500 to-rose-500" icon={Search} />
                <StatTile to="/bookmarks" label={t.bookmarks} value={bookmarkCount} accent="from-fuchsia-500 to-pink-500" icon={BookmarkCheck} />
            </div>

            {/* Quick access */}
            <h2 className="font-display text-xl font-bold mb-3">{t.quickAccess}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
                <QuickCard to="/classes" title={t.browseByClass} desc="Class 1 — Class 8" icon={Library} tid="quick-classes" gradient="from-primary/10 to-secondary/10" />
                <QuickCard to="/search" title={t.searchLO} desc={t.searchPlaceholder} icon={Search} tid="quick-search" gradient="from-accent/10 to-emerald-500/10" />
                <QuickCard to="/analytics" title={t.analytics} desc="Charts & distribution" icon={BarChart3} tid="quick-analytics" gradient="from-amber-500/10 to-rose-500/10" />
            </div>

            {/* Subject distribution preview */}
            {stats?.by_subject && stats.by_subject.length > 0 && (
                <>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display text-xl font-bold">{t.browseBySubject}</h2>
                        <Link to="/classes" className="text-xs text-primary font-semibold flex items-center gap-1">
                            {t.viewAll} <ArrowRight className="h-3 w-3" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
                        {stats.by_subject.map((s, i) => (
                            <motion.div
                                key={s.subject}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.04 }}
                            >
                                <button
                                    onClick={() => nav(`/search?subject=${encodeURIComponent(s.subject)}`)}
                                    data-testid={`subject-tile-${s.subject.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="w-full text-left rounded-3xl bg-card border border-border/70 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all"
                                >
                                    <div className={`h-10 w-10 rounded-2xl bg-gradient-to-br ${subjectGradient(s.subject)} grid place-items-center text-white mb-3 shadow-sm`}>
                                        <SubjectIcon subject={s.subject} className="h-5 w-5" />
                                    </div>
                                    <div className={`font-semibold text-sm ${lang === "od" ? "font-odia" : ""}`}>{subjectLabel(s.subject)}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{s.count} {t.codes}</div>
                                </button>
                            </motion.div>
                        ))}
                    </div>
                </>
            )}

            {/* Recent searches */}
            {recent.length > 0 && (
                <>
                    <div className="flex items-center gap-2 mb-3">
                        <History className="h-4 w-4 text-muted-foreground" />
                        <h2 className="font-display text-xl font-bold">{t.recentSearches}</h2>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-8">
                        {recent.slice(0, 8).map((r, i) => (
                            <Link
                                key={i}
                                to={`/search?q=${encodeURIComponent(r.query)}`}
                                data-testid={`recent-search-${i}`}
                                className="px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 text-xs font-medium active:scale-95 transition-all"
                            >
                                {r.query}
                            </Link>
                        ))}
                    </div>
                </>
            )}

            <div className="grid sm:grid-cols-2 gap-3 mb-12">
                <Link
                    to="/admin"
                    data-testid="dashboard-export-btn"
                    className="rounded-3xl bg-gradient-to-br from-primary to-secondary text-primary-foreground p-5 flex items-center justify-between active:scale-[0.98] transition-transform shadow-md"
                >
                    <div>
                        <div className="font-display font-bold text-lg">{t.exportAll}</div>
                        <div className="text-xs opacity-90">PDF • Excel • CSV • JSON</div>
                    </div>
                    <Download className="h-6 w-6" />
                </Link>
                <Link
                    to="/bookmarks"
                    className="rounded-3xl bg-card border border-border/70 p-5 flex items-center justify-between active:scale-[0.98] transition-transform"
                >
                    <div>
                        <div className="font-display font-bold text-lg">{t.bookmarks}</div>
                        <div className="text-xs text-muted-foreground">{bookmarkCount} saved</div>
                    </div>
                    <BookmarkCheck className="h-6 w-6 text-amber-500" />
                </Link>
            </div>
        </div>
    );
}

function StatTile({ to, label, value, accent, icon: Icon }) {
    return (
        <Link
            to={to}
            data-testid={`dashboard-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className="relative overflow-hidden rounded-3xl bg-card border border-border/70 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
        >
            <div className={`absolute -top-4 -right-4 h-20 w-20 rounded-full bg-gradient-to-br ${accent} opacity-20 blur-2xl`} />
            <div className={`relative h-10 w-10 rounded-2xl bg-gradient-to-br ${accent} grid place-items-center text-white shadow-sm mb-3`}>
                <Icon className="h-5 w-5" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">{label}</div>
            <div className="font-display text-2xl sm:text-3xl font-extrabold">
                {value !== undefined && value !== null ? Number(value).toLocaleString() : "—"}
            </div>
        </Link>
    );
}

function QuickCard({ to, title, desc, icon: Icon, tid, gradient }) {
    return (
        <Link
            to={to}
            data-testid={tid}
            className={`rounded-3xl bg-gradient-to-br ${gradient} border border-border/70 p-5 flex items-center gap-4 active:scale-[0.98] hover:shadow-md transition-all`}
        >
            <div className="h-12 w-12 rounded-2xl bg-card grid place-items-center shadow-sm">
                <Icon className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-display font-bold">{title}</div>
                <div className="text-xs text-muted-foreground truncate">{desc}</div>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </Link>
    );
}
